import { useMemo, useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import type { IngestJob } from "../intake/JobCard";

const SAMPLE: IngestJob[] = [
  {
    id: "job_aa11bb22cc",
    status: "indexed",
    source_type: "email",
    original_filename: "netbox-onboarding.docx",
    size_bytes: 240_000,
    detected_project: "netbox",
    detected_lang: "es",
    confidence: 0.94,
    target_doc_id: "doc_aa11bb22cc",
    created_at: "2026-05-15T08:30:00Z",
  },
  {
    id: "job_dd33ee44ff",
    status: "quarantined",
    source_type: "upload",
    original_filename: "config-server.txt",
    size_bytes: 12_000,
    detected_project: undefined,
    confidence: 0.41,
    created_at: "2026-05-15T08:45:00Z",
    error: "Confianza baja + posible secret AWS detectado",
  },
  {
    id: "job_gg55hh66ii",
    status: "converting",
    source_type: "url",
    original_filename: "https://blog.cloudflare.com/post.html",
    size_bytes: 80_000,
    created_at: "2026-05-15T09:00:00Z",
  },
  {
    id: "job_jj77kk88ll",
    status: "failed",
    source_type: "github",
    original_filename: "docs/architecture.md",
    size_bytes: 5_000,
    created_at: "2026-05-15T07:15:00Z",
    error: "Frontmatter inválido: 'project' no existe en taxonomía",
  },
  {
    id: "job_mm99nn00oo",
    status: "indexed",
    source_type: "paste",
    original_filename: "paste.md",
    size_bytes: 3_200,
    detected_project: "platform",
    confidence: 0.88,
    target_doc_id: "doc_mm99nn00oo",
    created_at: "2026-05-15T06:50:00Z",
  },
];

export function IngestDashboard() {
  const [filter, setFilter] = useState<IngestJob["status"] | "all">("all");
  const [q, setQ] = useState("");

  const items = useMemo(
    () =>
      SAMPLE.filter((j) => (filter === "all" ? true : j.status === filter)).filter((j) =>
        q ? j.original_filename.toLowerCase().includes(q.toLowerCase()) : true,
      ),
    [filter, q],
  );

  const counts = useMemo(() => {
    const out: Record<string, number> = { all: SAMPLE.length };
    for (const j of SAMPLE) out[j.status] = (out[j.status] ?? 0) + 1;
    return out;
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold">Admin · Ingest</h1>
        <p className="text-sm text-(--text-secondary)">
          Cola de jobs de ingesta, errores y promoción manual.
        </p>
      </header>

      <div className="mb-3 flex flex-wrap gap-2">
        {(
          ["all", "queued", "converting", "classified", "indexed", "failed", "quarantined"] as const
        ).map((s) => (
          <button
            type="button"
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full border px-3 py-1 text-xs ${
              filter === s
                ? "border-(--accent-500) bg-(--accent-50) text-(--accent-700)"
                : "border-(--border-subtle) hover:bg-(--bg-hover)"
            }`}
          >
            {s} <span className="ml-1 text-(--text-tertiary)">{counts[s] ?? 0}</span>
          </button>
        ))}
        <Input
          placeholder="Filtrar por filename…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="ml-auto max-w-xs"
        />
      </div>

      <table className="w-full text-sm">
        <thead className="text-left text-xs text-(--text-tertiary)">
          <tr className="border-b border-(--border-subtle)">
            <th className="py-2">ID</th>
            <th>Source</th>
            <th>Filename</th>
            <th>Project</th>
            <th>Conf.</th>
            <th>Status</th>
            <th>Created</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((j) => (
            <tr key={j.id} className="border-b border-(--border-subtle) hover:bg-(--bg-hover)">
              <td className="py-2 font-mono text-xs">{j.id}</td>
              <td>{j.source_type}</td>
              <td className="max-w-xs truncate">{j.original_filename}</td>
              <td>{j.detected_project ?? "—"}</td>
              <td>{j.confidence != null ? `${(j.confidence * 100).toFixed(0)}%` : "—"}</td>
              <td>
                <Badge
                  variant={
                    j.status === "indexed" ? "success" : j.status === "failed" ? "error" : "muted"
                  }
                  className="text-[10px]"
                >
                  {j.status}
                </Badge>
              </td>
              <td className="text-xs text-(--text-tertiary)">
                {new Date(j.created_at).toLocaleString()}
              </td>
              <td>
                <Button variant="ghost" size="sm">
                  Detalles
                </Button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={8} className="py-8 text-center text-(--text-tertiary)">
                Sin jobs que coincidan.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
